import React, { useRef, useCallback, useState, useMemo } from 'react';
import { FileTab } from '@/types';
import { FileIcon } from '../Decorations/FileIcon';
import { StatusIndicator } from '../Decorations/FileIcon';
import '../../styles/tabs.css';

interface TabProps {
  tab: FileTab;
  index: number;
  isActive: boolean;
  isSelected: boolean;
  isDragging: boolean;
  onActivate: () => void;
  onClose: () => void;
  onContextMenu: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
}

export const Tab: React.FC<TabProps> = React.memo(({
  tab,
  index,
  isActive,
  isSelected,
  isDragging,
  onActivate,
  onClose,
  onContextMenu,
  onDragStart,
  onDragEnd,
  onReorder,
}) => {
  const tabRef = useRef<HTMLDivElement>(null);
  const [isDragOverMe, setIsDragOverMe] = useState(false);
  const dragImageRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Left click
    if (e.button === 0) {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        // Multi-select behavior handled by parent
      } else {
        onActivate();
      }
    }
  }, [onActivate]);

  const handleDragStart = useCallback((e: React.DragEvent) => {
    e.dataTransfer!.effectAllowed = 'move';
    e.dataTransfer!.setData('tabId', tab.id);
    e.dataTransfer!.setData('tabIndex', index.toString());
    if (dragImageRef.current) {
      e.dataTransfer!.setDragImage(dragImageRef.current, 0, 0);
    }
    
    onDragStart();
  }, [tab.id, index, onDragStart]); 

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer!.dropEffect = 'move';
    setIsDragOverMe(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOverMe(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOverMe(false);
    const draggedTabId = e.dataTransfer!.getData('tabId');
    const fromIndex = parseInt(e.dataTransfer!.getData('tabIndex'), 10);

    if (draggedTabId !== tab.id && !isNaN(fromIndex)) {
      const toIndex = index;
      onReorder(fromIndex, toIndex);
    }
  }, [tab.id, index, onReorder]);

  const handleDragEnd = useCallback(() => {
    setIsDragOverMe(false);
    onDragEnd();
  }, [onDragEnd]);

  const displayName = useMemo(() => {
    if (tab.name.length > 20) {
      return tab.name.substring(0, 17) + '...';
    }
    return tab.name;
  }, [tab.name]);

  return (
    <>
      <div
        ref={tabRef}
        className={`tab ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${isDragOverMe ? 'drag-over' : ''} ${tab.isPreview ? 'preview' : ''}`}
        draggable
        role="tab"
        aria-selected={isActive}
        data-tab-id={tab.id}
        onMouseDown={handleMouseDown}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onDragEnd={handleDragEnd}
        onContextMenu={(e) => {
          e.preventDefault();
          onContextMenu();
        }}
      >
        <div className="tab-content">
          <FileIcon language={tab.language} />
          
          <span className={`tab-label ${tab.isPreview ? 'italic' : ''}`}>
            {displayName}
          </span>

          <StatusIndicator
            isDirty={tab.isDirty}
            isModified={tab.isModified}
            isPinned={tab.isPinned}
          />
        </div>

        <button
          className="tab-close"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label={`Close ${tab.name}`}
        >
          ✕
        </button>
      </div>

      {/* Hidden drag image */}
      <div
        ref={dragImageRef}
        className="drag-image"
        style={{
          position: 'fixed',
          left: '-9999px',
          top: '-9999px',
        }}
      >
        <div className="tab-drag-preview">
          <FileIcon language={tab.language} />
          <span>{displayName}</span>
        </div>
      </div>
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for React.memo to prevent unnecessary re-renders
  return (
    prevProps.tab.id === nextProps.tab.id &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isDragging === nextProps.isDragging &&
    prevProps.tab.name === nextProps.tab.name &&
    prevProps.tab.isDirty === nextProps.tab.isDirty &&
    prevProps.tab.isModified === nextProps.tab.isModified &&
    prevProps.tab.isPinned === nextProps.tab.isPinned &&
    prevProps.tab.isPreview === nextProps.tab.isPreview
  );
});

Tab.displayName = 'Tab';